import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import type { DateRange } from "react-day-picker";
import { formatDateRange } from "../lib/format";
import { Button } from "./button";
import { RangeCalendar } from "./range-calendar";
import { Typography } from "./typography";

type DateFilterProps = {
  onApply: (from?: Date, to?: Date) => void;
  error?: string;
  label?: string;
  from?: Date;
  to?: Date;
};

const CalendarFilterTrigger: FC<{
  isOpen: boolean;
  label: string;
  from?: Date;
  to?: Date;
}> = ({ isOpen, label, from, to }) => {
  const display = formatDateRange(from, to);

  return (
    <Button
      asChild
      variant="ghost"
      rightIcon={isOpen ? "chevron-up" : "chevron-down"}
      className="hover:cursor-pointer font-semibold"
    >
      {label}
      {(from || to) && (
        <Typography className="text-gray-700 font-normal">{display}</Typography>
      )}
    </Button>
  );
};

const DateFilterContent: FC<{
  onRangeChange: (range: DateRange | undefined) => void;
  from?: Date;
  to?: Date;
}> = ({ onRangeChange, from, to }) => {
  return <RangeCalendar onRangeChange={onRangeChange} from={from} to={to} />;
};

const DateFilter: FC<DateFilterProps> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [from, setFrom] = useState<Date | undefined>(props.from);
  const [to, setTo] = useState<Date | undefined>(props.to);
  useEffect(() => {
    setFrom(props.from);
    setTo(props.to);
  }, [props.from, props.to]);
  const onRangeChange = useCallback(
    (range: DateRange | undefined) => {
      setFrom(range?.from);
      setTo(range?.to);
      props.onApply(range?.from, range?.to);
    },
    [props.onApply],
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setIsOpen(!isOpen);
            }
          }}
        >
          <CalendarFilterTrigger
            from={from}
            to={to}
            isOpen={isOpen}
            label={props.label ?? "- Select date -"}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-2">
        <DateFilterContent onRangeChange={onRangeChange} from={from} to={to} />
      </PopoverContent>
    </Popover>
  );
};

export { DateFilter };
